import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { HtmlInjection } from '@components';
import { useAppDispatch, useAppSelector } from '@hooks/reduxHooks.ts';
import { useLingui } from '@lingui/react/macro';
import { getUserPreferencesOrDefaults } from '@routes/___shared___/utils.ts';
import { TASK_SCALE_TYPE } from '@routes/sidebar/___shared___/constants.ts';
import { Description, Guideline, RatingsWrapper, TaskWrapper } from '@routes/sidebar/___shared___/styled.tsx';
import type { TaskScaleType } from '@routes/sidebar/___shared___/types.ts';
import { getAssessorTask } from '@routes/sidebar/admin-center/assessor/___pages___/evaluate-task/model/evaluateTask.slice.ts';
import {
    getLearnerTask,
    getTaskAssessors,
} from '@routes/sidebar/learning/___pages___/learning-overview/model/task.slice.ts';
import type { AttemptLogStatus } from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/constants.ts';
import {
    ATTEMPT_LOG_STATUS,
    LOG_TITLE_COLOR_MAPPER,
    RATING_BACKGROUNDS,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/constants.ts';
import {
    EmptyMessage,
    EvidenceContent,
    Header,
    HeaderText,
    HeaderTextBold,
    LearnerInfo,
    LearnerName,
    Log,
    LogContent,
    LogHeader,
    LogsWrapper,
    PrintEvaluationLogWrapper,
    PrintSectionWrapper,
    PrintTaskWrapper,
    TaskInfo,
    Title,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/print-evaluation-log/styled.tsx';
import {
    CommentsContent,
    DateWrapper,
    Label,
    LogInfo,
    LogInfoText,
    LogInfoTextBold,
    LogInfoWrapper,
    Rating,
    ReferenceContentWrapper,
    SectionWrapper,
    StatusInfoWrapper,
    StatusTextWrapper,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/styled.tsx';
import {
    buildEvaluationLogEntries,
    getEvaluationLogEntryViewData,
    getLogPersonText,
} from '@routes/sidebar/learning/___pages___/learning-overview/ui/overview/task/utils.ts';
import { formatDateBasedOnPreferences } from '@utils/date.ts';
import { generateUniqueKeys } from '@utils/generators.ts';

interface Props {
    isAssessorView: boolean;
}

export const PrintEvaluationLog: React.FC<Props> = ({ isAssessorView }) => {
    const { pathwayId, taskId } = useParams<{
        pathwayId: string;
        taskId: string;
    }>();

    const { t } = useLingui();
    const dispatch = useAppDispatch();

    const userFullInfo = useAppSelector((state) => state.user.userInfo);
    const taskData = useAppSelector((state) =>
        isAssessorView ? state.assessorEvaluateTask.taskData : state.learningTask.taskData,
    );
    const assessors = useAppSelector((state) => state.learningTask.assessors);

    const { preferredDateFormat: dateFormat } = getUserPreferencesOrDefaults(userFullInfo);

    const STATUS_TEXT_LABELS = {
        redo: t`Redo`,
        closed: t`Closed`,
        exempted: t`Exempted`,
        exemptionRejected: t`Exemption rejected`,
        submitted: t`Submitted`,
    };

    const getLogStatusText = (status: AttemptLogStatus, attemptNumber: number) => {
        const LOG_STATUS_TEXT_MAPPER: Partial<Record<AttemptLogStatus, string>> = {
            [ATTEMPT_LOG_STATUS.PENDING]: t`Learner Submission (Attempt ${attemptNumber})`,
            [ATTEMPT_LOG_STATUS.PENDING_EXEMPTION]: t`Exemption Requested`,
            [ATTEMPT_LOG_STATUS.RECALLED]: t`Learner Submission (Attempt ${attemptNumber})`,
            [ATTEMPT_LOG_STATUS.REDO_REQUIRED]: t`Assessor Evaluation`,
            [ATTEMPT_LOG_STATUS.CLOSED]: t`Assessor Evaluation`,
            [ATTEMPT_LOG_STATUS.EXEMPTED]: t`Exempted`,
            [ATTEMPT_LOG_STATUS.EXEMPTION_REJECTED]: t`Exemption rejected`,
        };

        return LOG_STATUS_TEXT_MAPPER[status] ?? '';
    };

    const ratingScale = taskData?.taskConfiguration?.taskScaleType;

    const ratingScaleOptions: Record<TaskScaleType, string[]> = {
        [TASK_SCALE_TYPE.TwoPointApprove]: [t`Disapproved`, t`Approved`],
        [TASK_SCALE_TYPE.TwoPointConfidence]: [t`Not Confident`, t`Confident`],
        [TASK_SCALE_TYPE.FivePointCompetence]: [
            t`Unsatisfactory`,
            t`Needs improvement`,
            t`Meets expectations`,
            t`Exceeds expectations`,
            t`Exceptional`,
        ],
    };

    const ratings = ratingScale ? ratingScaleOptions[ratingScale] : undefined;
    const backgrounds = ratingScale ? RATING_BACKGROUNDS[ratingScale] : undefined;

    const evaluationLogEntries = buildEvaluationLogEntries(taskData?.evaluationLog ?? [], 'asc');

    useEffect(() => {
        if (!pathwayId || !taskId) {
            return;
        }

        if (isAssessorView) {
            void dispatch(
                getAssessorTask({
                    userPathwayId: Number(pathwayId),
                    pathwayItemId: Number(taskId),
                }),
            );

            return;
        }

        void dispatch(
            getLearnerTask({
                pathwayId,
                pathwayItemId: Number(taskId),
            }),
        );
        void dispatch(getTaskAssessors());
    }, [dispatch, pathwayId, taskId, isAssessorView]);

    return (
        <PrintEvaluationLogWrapper>
            {!!taskData && (
                <>
                    <Header>
                        <Title>{t`Evaluation log`}</Title>

                        <LearnerInfo>
                            <LearnerName>
                                {[taskData.details.name, taskData.details.rankName, taskData.details.vesselName]
                                    .filter(Boolean)
                                    .join(', ')}
                            </LearnerName>

                            <HeaderText>
                                <HeaderTextBold>{t`Pathway title:`} </HeaderTextBold>
                                {taskData.details.pathway}
                            </HeaderText>
                        </LearnerInfo>
                    </Header>

                    <TaskInfo>
                        {!!taskData.details.sectionName && (
                            <HeaderText>
                                <HeaderTextBold>{t`Section:`} </HeaderTextBold>
                                {taskData.details.sectionName}
                            </HeaderText>
                        )}

                        {!!taskData.details.subSectionName && (
                            <HeaderText>
                                <HeaderTextBold>{t`Sub-section:`} </HeaderTextBold>
                                {taskData.details.subSectionName}
                            </HeaderText>
                        )}

                        <HeaderText>
                            <HeaderTextBold>{t`Task title:`} </HeaderTextBold>
                            {taskData.details.taskName}
                        </HeaderText>
                    </TaskInfo>

                    {evaluationLogEntries.length === 0 && (
                        <EmptyMessage>{t`No evaluation log entries available.`}</EmptyMessage>
                    )}

                    <LogsWrapper>
                        {evaluationLogEntries.map((entry, entryIndex) => {
                            const { attempt, status, isAssessorEvaluation, displayDateUTC, key } = entry;
                            const { showStatusPill, showAssessorComments, statusLabel, StatusIcon, isInactiveRatings } =
                                getEvaluationLogEntryViewData(entry, STATUS_TEXT_LABELS);

                            const pageBreakAfter = showAssessorComments && entryIndex < evaluationLogEntries.length - 1;

                            return (
                                <Log key={key} $pageBreakAfter={pageBreakAfter}>
                                    <LogHeader $status={status}>
                                        <LogInfoWrapper>
                                            <LogInfo>
                                                <LogInfoTextBold $color={LOG_TITLE_COLOR_MAPPER[status]}>
                                                    {getLogStatusText(status, attempt.number)}
                                                </LogInfoTextBold>

                                                <LogInfoText>
                                                    {getLogPersonText(
                                                        taskData.details.name,
                                                        taskData.details.rankName,
                                                        attempt.assessorId,
                                                        assessors,
                                                        attempt.assessorName,
                                                        attempt.assessorRank,
                                                    )}
                                                </LogInfoText>
                                            </LogInfo>

                                            <LogInfo>
                                                <LogInfoText>
                                                    <DateWrapper>
                                                        {formatDateBasedOnPreferences(displayDateUTC, dateFormat)}
                                                    </DateWrapper>
                                                </LogInfoText>

                                                {!!showStatusPill && (
                                                    <StatusInfoWrapper>
                                                        {!!StatusIcon && <StatusIcon />}

                                                        <StatusTextWrapper>
                                                            <LogInfoTextBold $color={LOG_TITLE_COLOR_MAPPER.TEXT_BODY}>
                                                                {t`Status: `}
                                                            </LogInfoTextBold>

                                                            <LogInfoText>{statusLabel}</LogInfoText>
                                                        </StatusTextWrapper>
                                                    </StatusInfoWrapper>
                                                )}
                                            </LogInfo>
                                        </LogInfoWrapper>
                                    </LogHeader>

                                    <LogContent>
                                        {!!ratings &&
                                            !!backgrounds &&
                                            generateUniqueKeys(taskData.taskConfiguration.subTasks).map(
                                                ({ item: task, key: taskKey }, taskIndex) => {
                                                    const learnerRating = attempt.learnerAnswers.find(
                                                        (answer) => answer.order === taskIndex,
                                                    )?.points;

                                                    const assessorRating = attempt.assessorAnswers.find(
                                                        (answer) => answer.order === taskIndex,
                                                    )?.points;

                                                    const selectedRating = isAssessorEvaluation
                                                        ? assessorRating
                                                        : learnerRating;
                                                    const compareRating = isAssessorEvaluation ? learnerRating : null;

                                                    return (
                                                        <PrintTaskWrapper key={taskKey}>
                                                            <TaskWrapper>
                                                                <Description>
                                                                    {`${taskIndex + 1}.`} {task.description}
                                                                </Description>

                                                                <Guideline>{task.guideline}</Guideline>

                                                                <RatingsWrapper>
                                                                    {ratings.map((rating, ratingIndex) => (
                                                                        <Rating
                                                                            key={rating}
                                                                            $isInactive={isInactiveRatings}
                                                                            $isSelected={
                                                                                !isInactiveRatings &&
                                                                                selectedRating === ratingIndex + 1
                                                                            }
                                                                            $hasAssessorOrAssesseeSelection={
                                                                                !isInactiveRatings &&
                                                                                compareRating === ratingIndex + 1
                                                                            }
                                                                            $selectedBackground={
                                                                                backgrounds[ratingIndex]
                                                                            }
                                                                        >
                                                                            {rating}
                                                                        </Rating>
                                                                    ))}
                                                                </RatingsWrapper>
                                                            </TaskWrapper>
                                                        </PrintTaskWrapper>
                                                    );
                                                },
                                            )}

                                        {!!taskData.taskConfiguration.referenceText && (
                                            <PrintSectionWrapper>
                                                <SectionWrapper>
                                                    <Label>{t`Reference`}</Label>

                                                    <ReferenceContentWrapper>
                                                        <HtmlInjection
                                                            text={taskData.taskConfiguration.referenceText}
                                                        />
                                                    </ReferenceContentWrapper>
                                                </SectionWrapper>
                                            </PrintSectionWrapper>
                                        )}

                                        {!!attempt.learnerComments && (
                                            <PrintSectionWrapper>
                                                <SectionWrapper>
                                                    <Label>
                                                        {attempt.isExemption
                                                            ? t`Reason for Exemption`
                                                            : t`Assessee's Comments`}
                                                    </Label>

                                                    <CommentsContent>{attempt.learnerComments}</CommentsContent>
                                                </SectionWrapper>
                                            </PrintSectionWrapper>
                                        )}

                                        {!!attempt.evidenceFileId && (
                                            <PrintSectionWrapper>
                                                <SectionWrapper>
                                                    <Label>{t`Evidence File`}</Label>

                                                    <EvidenceContent>
                                                        {attempt.evidenceFileName ?? t`Uploaded evidence`}
                                                    </EvidenceContent>
                                                </SectionWrapper>
                                            </PrintSectionWrapper>
                                        )}

                                        {!!showAssessorComments && (
                                            <PrintSectionWrapper>
                                                <SectionWrapper>
                                                    <Label>{t`Assessor's Comments`}</Label>

                                                    <CommentsContent>{attempt.assessorComments}</CommentsContent>
                                                </SectionWrapper>
                                            </PrintSectionWrapper>
                                        )}
                                    </LogContent>
                                </Log>
                            );
                        })}
                    </LogsWrapper>
                </>
            )}
        </PrintEvaluationLogWrapper>
    );
};
